import { useState, useRef, useEffect } from 'react';
import { Send, Loader, ChevronLeft, ChevronRight, MessageCircle, User } from 'lucide-react';
import aiChatService from '../services/aiChatService';
import ReactMarkdown from 'react-markdown';
import logoIcon from '../assets/logo3.png';

const PRE_BUILT_COMMANDS = [
  { command: '/audit', description: 'Run a full AI-Trust audit of my organisation.' },
  { command: '/arch', description: 'Describe my AI architecture risks.' },
  { command: '/train', description: 'Suggest improvements to my model training governance.' },
  { command: '/security', description: 'Summarise my AI-security posture.' },
  { command: '/privacy', description: 'Summarise privacy compliance gaps.' },
  { command: '/performance', description: 'Show model-performance trends.' },
  { command: '/hallucination', description: 'List hallucination incidents in my estate.' },
  { command: '/safety', description: 'List my AI-safety controls.' },
  { command: '/usage', description: 'Show AI-tool usage metrics.' }
];

// Add a function to format the AI response for better readability
function formatAIResponse(text) {
  // Add line breaks after each dimension bullet and before summary lines
  return text
    .replace(/- (Security|Privacy|Governance|Fairness\/Bias|Transparency|Enablement):/g, '\n- $1:')
    .replace(/(No evidence yet\.|No assessment yet\.|No grade yet\.)/g, '$1\n')
    .replace(/(Since no assessment|As a practical next step|Given this,|Summary:)/g, '\n$1')
    .replace(/\n{2,}/g, '\n\n'); // Ensure no excessive line breaks
}

export default function AiChat({ companyId }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [chatWidth, setChatWidth] = useState(400); // default width in px
  const [isResizing, setIsResizing] = useState(false);
  const chatRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! 👋 I'm your Quantifaid assistant. How can I help today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCommands, setShowCommands] = useState(false);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const commandsListRef = useRef(null);
  const lastWidth = useRef(chatWidth);

  // Update document root style for main content margin
  useEffect(() => {
    const root = document.documentElement;
    if (isCollapsed) {
      root.style.setProperty('--chat-width', '40px');
    } else {
      root.style.setProperty('--chat-width', `${chatWidth}px`);
    }
  }, [isCollapsed, chatWidth]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isCollapsed]);

  // Handle drag to resize
  useEffect(() => {
    function handleMouseMove(e) {
      if (!isResizing) return;
      const minWidth = 320;
      const maxWidth = 800;
      const newWidth = Math.min(Math.max(e.clientX, minWidth), maxWidth);
      setChatWidth(newWidth);
      document.documentElement.style.setProperty('--chat-width', `${newWidth}px`);
    }
    function handleMouseUp() {
      setIsResizing(false);
    }
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    if (value === '/') {
      setShowCommands(true);
      setSelectedCommandIndex(0);
    } else if (value.startsWith('/')) {
      const filteredCommands = PRE_BUILT_COMMANDS.filter(cmd => 
        cmd.command.toLowerCase().includes(value.toLowerCase())
      );
      if (filteredCommands.length > 0) {
        setShowCommands(true);
        setSelectedCommandIndex(0);
      } else {
        setShowCommands(false);
      }
    } else {
      setShowCommands(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showCommands) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedCommandIndex(prev => 
          Math.min(prev + 1, PRE_BUILT_COMMANDS.length - 1)
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedCommandIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (showCommands && PRE_BUILT_COMMANDS[selectedCommandIndex]) {
          setInput(PRE_BUILT_COMMANDS[selectedCommandIndex].command + ' ');
          setShowCommands(false);
          inputRef.current?.focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowCommands(false);
        break;
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setError(null);
    setIsLoading(true);
    setShowCommands(false);
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setMessages(prev => [...prev, { role: 'assistant', content: '...', loading: true }]);
    try {
      const aiResponse = await aiChatService.sendMessage(companyId, userMessage);
      setMessages(prev => prev.filter(msg => !msg.loading));
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      setError(error.message || 'Failed to send message. Please try again.');
      setMessages(prev => prev.filter(msg => !msg.loading));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCommandClick = (command) => {
    setInput(command + ' ');
    setShowCommands(false);
    inputRef.current?.focus();
  };

  // Collapsed chat button
  if (isCollapsed) {
    return (
      <div className="fixed top-0 left-0 h-full flex items-center z-40">
        <button
          onClick={() => {
            setIsCollapsed(false);
            setChatWidth(lastWidth.current);
          }}
          className="bg-primary text-background p-2 rounded-r-lg shadow-lg hover:bg-primary-light transition-all flex items-center gap-2"
          aria-label="Expand chat"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    );
  }

  // Expanded chat
  return (
    <div
      ref={chatRef}
      className="fixed top-0 left-0 z-40 h-screen bg-background border-r border-primary/20 shadow-2xl flex flex-col"
      style={{ width: chatWidth, minWidth: 320, maxWidth: 800 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-primary/20">
        <h2 className="text-lg font-semibold text-text">Quantifaid AI Assistant</h2>
        <button
          onClick={() => {
            lastWidth.current = chatWidth;
            setIsCollapsed(true);
          }}
          className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
          aria-label="Collapse chat"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      {/* Messages Section */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) =>
          message.role === 'assistant' ? (
            <div key={index} className="flex items-start w-full gap-3">
              <div className="flex-shrink-0 flex items-start pt-1" style={{ width: 40, minWidth: 40 }}>
                <img
                  src={logoIcon}
                  alt="Quantifaid AI"
                  className="w-8 h-8 rounded-full bg-background-light border border-primary/20"
                />
              </div>
              <div
                className="max-w-[80%] rounded-lg p-3 whitespace-pre-line relative bg-background-light border border-primary/20 text-text shadow-md"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {formatAIResponse(message.content)}
              </div>
            </div>
          ) : (
            <div key={index} className="flex items-start justify-end w-full gap-3">
              <div
                className="max-w-[80%] rounded-lg p-3 whitespace-pre-line bg-primary text-background relative"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {message.content}
              </div>
              <div className="flex-shrink-0 flex items-start pt-1" style={{ width: 40, minWidth: 40 }}>
                <div className="w-8 h-8 rounded-full bg-primary/20 border border-primary flex items-center justify-center">
                  <User size={16} className="text-primary" />
                </div>
              </div>
            </div>
          )
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-background-light rounded-lg p-3">
              <Loader className="animate-spin" size={16} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-4 py-2 bg-danger/10 text-danger text-sm">
          {error}
        </div>
      )}

      {/* Input Section */}
      <div className="p-4 border-t border-primary/20 relative">
        {showCommands && (
          <div 
            ref={commandsListRef}
            className="absolute bottom-full left-4 right-4 mb-2 bg-background border border-primary/20 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {PRE_BUILT_COMMANDS.map((cmd, index) => (
              <button
                key={cmd.command}
                onClick={() => handleCommandClick(cmd.command)}
                className={`w-full px-4 py-2 text-left hover:bg-primary/10 ${
                  index === selectedCommandIndex ? 'bg-primary/10' : ''
                }`}
              >
                <div className="font-medium text-primary">{cmd.command}</div>
                <div className="text-sm text-text-secondary">{cmd.description}</div>
              </button>
            ))}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message or use a command..."
              className="flex-1 bg-background-light rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className={`p-2 rounded-lg transition-colors ${
                isLoading || !input.trim()
                  ? 'bg-primary/20 text-text-secondary cursor-not-allowed'
                  : 'bg-primary text-background hover:bg-primary-light'
              }`}
            >
              {isLoading ? <Loader className="animate-spin" size={20} /> : <Send size={20} />}
            </button>
          </div>
        </form>
      </div>

      {/* Resize handle */}
      <div
        className="absolute top-0 right-0 h-full w-2 cursor-ew-resize z-50 bg-transparent hover:bg-primary/10 transition-colors"
        onMouseDown={() => setIsResizing(true)}
        title="Drag to resize"
      />
    </div>
  );
} 